import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import QRCode from "qrcode";
import { Link } from "react-router-dom";
import { Printer, FileDown, ArrowLeft, Layers, CircleDot, ScrollText, FolderInput, RefreshCw, Save, Send, BadgeCheck } from "lucide-react";
import {
  Doc0SubjectNote,
  Doc0DiscountDraft,
  Doc6PurchaseAssignment,
  Doc6AAllonge,
  Doc6BBillOfSale,
  Doc6CNoticeToMaker,
  Doc6DClosingCert,
  Doc6EIncumbency,
  Doc6FProceedsDirection,
  Doc6GDisbursementAck,
  DocD01DealerTermSheet,
} from "@/components/documents/Documents";
import { defaultDeal, fmtCurrency, fmtDate } from "@/lib/format";
import { FIRM } from "@/lib/firm";
import { hashStr, GoldFoilSeal } from "@/components/documents/_shared";
import { CounterpartyAutocomplete } from "@/components/CounterpartyAutocomplete";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Path 1 — Outright Note Sale document chain (UCC Art. 3 negotiation).
const SALE_DOCS = [
  { id: "doc6e", label: "06-E · Incumbency Certificate", component: Doc6EIncumbency },
  { id: "doc6d", label: "06-D · Closing Certificate", component: Doc6DClosingCert },
  { id: "doc6", label: "06 · Purchase & Assignment", component: Doc6PurchaseAssignment },
  { id: "doc6a", label: "06-A · Allonge", component: Doc6AAllonge },
  { id: "doc6b", label: "06-B · Bill of Sale", component: Doc6BBillOfSale },
  { id: "doc6c", label: "06-C · Notice to Buyer", component: Doc6CNoticeToMaker },
  { id: "doc6f", label: "06-F · Proceeds Direction", component: Doc6FProceedsDirection },
  { id: "doc6g", label: "06-G · Disbursement Receipt", component: Doc6GDisbursementAck },
  { id: "doc0", label: "00 · Subject Note (Exhibit A)", component: Doc0SubjectNote },
  { id: "doc0a", label: "00-A · Private Negotiable Instrument (Exhibit B)", component: Doc0DiscountDraft },
  { id: "docd01", label: "D-01 · Dealer Term Sheet", component: DocD01DealerTermSheet },
];

const todayIso = () => new Date().toISOString().slice(0, 10);

// Cover + Table of Contents rendered as a print-eligible paper document.
const CoverPage = React.forwardRef(({ d }, ref) => (
  <div className="paper-doc" ref={ref} data-testid="nsb-cover">
    <div style={{ textAlign: "center", paddingTop: "0.4in" }}>
      <div className="eyebrow" style={{ letterSpacing: "0.22em" }}>Path 1 · UCC Article 3 · Without Recourse</div>
      <h1 className="doc-title" style={{ fontSize: "30pt", marginTop: 10, color: "var(--boa-blue, #0B4636)" }}>
        Outright Note Sale
      </h1>
      <h2 className="doc-sub" style={{ marginTop: 4 }}>
        Negotiation &amp; Discount Purchase of a Promissory Note · Closing Binder
      </h2>
      <div style={{ display: "flex", justifyContent: "center", marginTop: 10 }}>
        <GoldFoilSeal size={120} label="Seutrade Finance · Financial Services" />
      </div>
      <hr className="doc-rule" style={{ margin: "22px auto", maxWidth: "70%" }} />
    </div>

    <table className="kv-table" style={{ margin: "0 auto", width: "78%" }} data-testid="nsb-cover-parties">
      <tbody>
        <tr><td style={{ fontWeight: 600, width: "40%" }}>Seller (Holder)</td><td>{d.borrowerName || "—"}</td></tr>
        <tr><td style={{ fontWeight: 600 }}>Buyer (Purchaser)</td><td>{d.buyerName || "TO BE DESIGNATED"}</td></tr>
        <tr><td style={{ fontWeight: 600 }}>Maker of Subject Note</td><td>{d.makerName || "—"}</td></tr>
        <tr><td style={{ fontWeight: 600 }}>Accepting / Acceptance Bank</td><td>{d.acceptanceBankName || "—"}</td></tr>
        <tr><td style={{ fontWeight: 600 }}>Face Value</td><td>{fmtCurrency(d.faceValue)}</td></tr>
        <tr><td style={{ fontWeight: 600 }}>Purchase Price</td><td>{fmtCurrency(d.purchasePrice)}</td></tr>
        <tr><td style={{ fontWeight: 600 }}>Discount of Face</td><td>{d.faceValue ? (100 - (d.purchasePrice / d.faceValue) * 100).toFixed(2) : "0.00"}%</td></tr>
        <tr><td style={{ fontWeight: 600 }}>Sale Closing Date</td><td>{fmtDate(d.saleClosingDate)}</td></tr>
        <tr><td style={{ fontWeight: 600 }}>Governing Law</td><td>State of {d.governingLaw || "—"}</td></tr>
      </tbody>
    </table>

    <div style={{ margin: "30px auto 0", width: "78%" }}>
      <div className="doc-h" style={{ fontWeight: 700, fontSize: "12pt", borderBottom: "1.5px solid #0B4636", paddingBottom: 4, marginBottom: 8 }}>
        Table of Contents
      </div>
      <ol style={{ margin: 0, paddingLeft: "1.2em", fontSize: "11pt", lineHeight: 1.9 }} data-testid="nsb-cover-toc">
        {SALE_DOCS.map((doc) => (
          <li key={doc.id}>{doc.label}</li>
        ))}
      </ol>
    </div>

    <div className="doc-footer" style={{ textAlign: "center", marginTop: 34, fontSize: "8.5pt", letterSpacing: "0.14em", textTransform: "uppercase", color: "#5C5C5C" }}>
      SEUTRADE FINANCIAL SERVICES · Note Liquidity Desk · Sale Without Recourse (UCC § 3-415)
    </div>
  </div>
));

export default function NoteSaleBinder() {
  const [d, setD] = useState(() => ({
    ...defaultDeal,
    saleClosingDate: todayIso(),
    // Seutrade holds & sells the bank note; Commercial Bancorp Holdings is the maker.
    borrowerName: "SEUTRADE FINANCIAL SERVICES",
    borrowerAddress: FIRM.address,
    signerTitle: "Authorized Officer · Seutrade Financial Services",
    makerName: "COMMERCIAL BANCORP HOLDINGS",
    makerSsn: "",
    buyerName: "TO BE DESIGNATED (NEW PURCHASER)",
    treasuryHouseName: "Seutrade Finance",
    treasuryHouseTagline: "Financial Services · Private Negotiable Instrument",
    regNo: FIRM.ein,
    intlRegNo: "",
    purWireBank: "", purWireRouting: "", purWireSwift: "", purWireAcctName: "", purWireAcctNo: "",
    payWireBank: "", payWireRouting: "", payWireSwift: "", payWireAcctName: "", payWireAcctNo: "",
  }));
  const [activeDoc, setActiveDoc] = useState("cover");
  const [printMode, setPrintMode] = useState(null); // 'single' | 'all' | null
  const [loans, setLoans] = useState([]);
  const [selectedLoan, setSelectedLoan] = useState("");
  const [loadingLoans, setLoadingLoans] = useState(false);
  const [prefillNote, setPrefillNote] = useState("");
  const [savedAt, setSavedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [faxTo, setFaxTo] = useState("");
  const [faxing, setFaxing] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");
  const [minting, setMinting] = useState(false);

  const setField = (name, value) => setD((p) => ({ ...p, [name]: value }));

  // ---- E-signatures (E-SIGN Act / UETA) ----
  const SIG_ROLES = [
    { role: "seller", label: "Seller (Holder)", nameField: "signerName", titleField: "signerTitle" },
    { role: "trustee", label: "Independent Co-Trustee", nameField: "cotrusteeName", titleField: "cotrusteeTitle" },
    { role: "buyer", label: "Buyer (Purchaser)", nameField: "buyerSigner", titleField: "buyerTitle" },
    { role: "acceptance", label: "Accepting Bank Officer", nameField: "acceptanceOfficer", titleField: "acceptanceOfficerTitle" },
    { role: "notary", label: "Notary Public", nameField: "notaryName", titleField: "notaryTitle" },
  ];
  const hasSig = (role) => (d.signatures || []).some((s) => s.signer_role === role);
  const fingerprintOf = (seed) =>
    ["", "|1", "|2", "|3"].map((salt) => hashStr(seed + salt).toString(16)).join("").padEnd(24, "0");
  const applySignature = (role, nameField) => {
    const name = (d[nameField] || "").trim();
    if (!name) { setStatusMsg(`Enter a name for the ${role} before signing.`); return; }
    const signed_at = new Date().toISOString();
    const fingerprint = fingerprintOf(`${role}|${name}|${signed_at}`);
    setD((p) => ({
      ...p,
      signatures: [
        ...(p.signatures || []).filter((s) => s.signer_role !== role),
        { signer_role: role, signer_name: name, signed_at, fingerprint },
      ],
    }));
  };
  const clearSignature = (role) => setD((p) => ({ ...p, signatures: (p.signatures || []).filter((s) => s.signer_role !== role) }));
  const applyAllSignatures = () => {
    const signed_at = new Date().toISOString();
    const sigs = SIG_ROLES
      .filter((r) => (d[r.nameField] || "").trim())
      .map((r) => ({ signer_role: r.role, signer_name: d[r.nameField].trim(), signed_at, fingerprint: fingerprintOf(`${r.role}|${d[r.nameField]}|${signed_at}`) }));
    setD((p) => ({ ...p, signatures: sigs }));
    setStatusMsg(`✓ Applied ${sigs.length} e-signature(s).`);
  };

  const token = useMemo(() => localStorage.getItem("th_admin_token") || "", []);

  const fetchLoans = async () => {
    setLoadingLoans(true);
    try {
      const r = await axios.get(`${API}/admin/borrower-applications`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const all = [...(r.data?.pending || []), ...(r.data?.resolved || [])];
      setLoans(all);
    } catch (e) {
      setLoans([]);
    } finally {
      setLoadingLoans(false);
    }
  };

  useEffect(() => {
    fetchLoans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const prefillFromLoan = (appId) => {
    setSelectedLoan(appId);
    setSavedAt(null);
    if (!appId) {
      setPrefillNote("");
      return;
    }
    const app = loans.find((x) => x.id === appId);
    if (!app) return;
    // If a Note Sale binder was already saved to this loan, restore it verbatim.
    if (app.note_sale_package && app.note_sale_package.data) {
      setD((p) => ({ ...p, ...app.note_sale_package.data }));
      setSavedAt(app.note_sale_package.saved_at || null);
      setPrefillNote(`Restored saved Note Sale binder · ${app.borrower_name || appId}`);
      return;
    }
    const b = app.binder || {};
    const face = Number(b.payoffAmount || app.current_balance || 0) || 0;
    setD((p) => ({
      ...p,
      makerName: (b.borrowerName || app.borrower_name || p.makerName).toUpperCase(),
      makerAddress: b.borrowerAddress || p.makerAddress,
      faceValue: face || p.faceValue,
      purchasePrice: face ? Math.round(face * 0.85) : p.purchasePrice,
      couponRate: Number(b.newRate || p.couponRate) || p.couponRate,
      executionDate: b.closingDate || p.executionDate,
      maturityDate: (b.tila && b.tila.maturity_date) || p.maturityDate,
      saleClosingDate: todayIso(),
      governingLaw: b.governingLaw || p.governingLaw,
    }));
    setPrefillNote(`Prefilled from loan · ${app.borrower_name || appId} · ${fmtCurrency(face)} face`);
  };

  const saveToLoan = async () => {
    if (!selectedLoan) return;
    setSaving(true); setStatusMsg("");
    try {
      const r = await axios.put(
        `${API}/admin/borrower-applications/${selectedLoan}/note-sale-package`,
        { data: d },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSavedAt(r.data?.saved_at || new Date().toISOString());
      setStatusMsg("✓ Saved to loan file.");
    } catch (e) {
      setStatusMsg("⚠ " + (e?.response?.data?.detail || "Save failed."));
    }
    setSaving(false);
  };

  const efax = async () => {
    if (!faxTo.trim()) { setStatusMsg("Enter the buyer's fax number (E.164, e.g. +13125551234)."); return; }
    setFaxing(true); setStatusMsg("");
    try {
      const r = await axios.post(
        `${API}/admin/fax/send-note-sale`,
        { to: faxTo, deal: d },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStatusMsg(`✓ eFax queued (id ${r.data.fax_id}).`);
    } catch (e) {
      setStatusMsg("⚠ " + (e?.response?.data?.detail || "eFax failed."));
    }
    setFaxing(false);
  };

  const mintCert = async () => {
    setMinting(true); setStatusMsg("");
    try {
      const r = await axios.post(
        `${API}/admin/note-sale/mint`,
        {
          sellerName: d.borrowerName, buyerName: d.buyerName, makerName: d.makerName,
          acceptanceBankName: d.acceptanceBankName, faceValue: d.faceValue, purchasePrice: d.purchasePrice,
          couponRate: d.couponRate, termDays: d.termDays, executionDate: d.executionDate,
          maturityDate: d.maturityDate, saleClosingDate: d.saleClosingDate,
          draftNo: d.draftNo || d.reference || "", regNo: d.regNo, intlRegNo: d.intlRegNo, appId: selectedLoan || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { serial, verify_token } = r.data;
      const verifyUrl = `${window.location.origin}/verify-note/${verify_token}`;
      const qr = await QRCode.toDataURL(verifyUrl, { margin: 1, width: 240, errorCorrectionLevel: "H" });
      setD((p) => ({ ...p, mintedSerial: serial, verifyToken: verify_token, verifyUrl, verifyQr: qr }));
      setStatusMsg(`✓ Minted serial ${serial} · verification QR affixed to the Bank Note.`);
    } catch (e) {
      setStatusMsg("⚠ " + (e?.response?.data?.detail || "Mint failed."));
    }
    setMinting(false);
  };

  useEffect(() => {
    const handler = () => setPrintMode(null);
    window.addEventListener("afterprint", handler);
    return () => window.removeEventListener("afterprint", handler);
  }, []);

  const printDocument = (which = "single") => {
    setPrintMode(which);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.print();
        setTimeout(() => setPrintMode((m) => (m === which ? null : m)), 500);
      });
    });
  };

  const ActiveComp = activeDoc === "cover" ? null : SALE_DOCS.find((x) => x.id === activeDoc)?.component;

  const field = (label, name, type = "text") => (
    <label className="block">
      <span className="field-label">{label}</span>
      <input
        type={type}
        value={d[name] ?? ""}
        onChange={(e) => setField(name, type === "number" ? Number(e.target.value) : e.target.value)}
        className="w-full mt-0.5 px-2 py-1.5 text-[13px] border border-[#D8D8CF] rounded-sm bg-white focus:border-[#0B4636] focus:outline-none"
        data-testid={`nsb-field-${name}`}
      />
    </label>
  );

  return (
    <div className="app-shell min-h-screen bg-[#F5F5F0]">
      {/* Top bar */}
      <header className="no-print sticky top-0 z-30 border-b border-[#E5E5DF] bg-white/95 backdrop-blur">
        <div className="flex items-center justify-between px-6 py-3">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-sm bg-[#0B4636] flex items-center justify-center">
              <ScrollText size={15} color="#fff" strokeWidth={2.2} />
            </div>
            <div className="leading-tight">
              <div className="font-serif-display text-[18px] font-semibold tracking-tight">Outright Note Sale Binder</div>
              <div className="text-[10px] tracking-[0.18em] uppercase text-[#5C5C5C] -mt-0.5">UCC Art. 3 · Without Recourse</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/admin" className="flex items-center gap-1.5 text-[12px] text-[#5C5C5C] hover:text-[#0A0A0A]" data-testid="nsb-back-admin">
              <ArrowLeft size={14} /> Admin
            </Link>
            <button
              onClick={() => printDocument("single")}
              disabled={activeDoc === "cover" ? false : !ActiveComp}
              className="flex items-center gap-1.5 text-[12px] px-3 py-1.5 border border-[#0B4636] text-[#0B4636] rounded-sm hover:bg-[#0B4636] hover:text-white transition-colors"
              data-testid="nsb-print-single"
            >
              <Printer size={13} /> Print this document
            </button>
            <button
              onClick={() => printDocument("all")}
              className="flex items-center gap-1.5 text-[12px] px-3 py-1.5 bg-[#0B4636] text-white rounded-sm hover:bg-[#0d5842] transition-colors"
              data-testid="nsb-print-all"
            >
              <FileDown size={13} /> Print full binder
            </button>
          </div>
        </div>
      </header>

      {/* Body (screen only) */}
      <div className="no-print grid grid-cols-12 gap-0 min-h-[calc(100vh-58px)]">
        {/* Sidebar — intake + prefill + TOC */}
        <aside className="col-span-4 xl:col-span-3 border-r border-[#E5E5DF] bg-white overflow-y-auto max-h-[calc(100vh-58px)]">
          <div className="p-5 border-b border-[#E5E5DF]">
            <div className="flex items-center gap-2 mb-2 text-[#0B4636]">
              <FolderInput size={15} />
              <span className="eyebrow">Prefill from a loan</span>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={selectedLoan}
                onChange={(e) => prefillFromLoan(e.target.value)}
                className="flex-1 px-2 py-1.5 text-[12.5px] border border-[#D8D8CF] rounded-sm bg-white focus:border-[#0B4636] focus:outline-none"
                data-testid="nsb-loan-select"
              >
                <option value="">— Optional: pick a borrower loan —</option>
                {loans.map((a) => (
                  <option key={a.id} value={a.id}>
                    {(a.borrower_name || "Borrower")} · {fmtCurrency(a.binder?.payoffAmount || a.current_balance || 0)}
                  </option>
                ))}
              </select>
              <button
                onClick={fetchLoans}
                title="Refresh loans"
                className="p-1.5 border border-[#D8D8CF] rounded-sm text-[#5C5C5C] hover:text-[#0B4636] hover:border-[#0B4636]"
                data-testid="nsb-refresh-loans"
              >
                <RefreshCw size={14} className={loadingLoans ? "animate-spin" : ""} />
              </button>
            </div>
            {prefillNote && <div className="mt-2 text-[11px] text-[#0B6B4F]" data-testid="nsb-prefill-note">{prefillNote}</div>}
            <p className="mt-2 text-[10.5px] text-[#8A8A8A] leading-snug">Editable below — a loan just seeds the parties, amount and dates. No loan needed.</p>

            {/* Save + transmit */}
            <div className="mt-4 pt-4 border-t border-[#E5E5DF] space-y-2">
              <button
                onClick={saveToLoan}
                disabled={!selectedLoan || saving}
                data-testid="nsb-save-btn"
                className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-sm bg-[#0B4636] text-white text-[12px] font-semibold hover:bg-[#0d5842] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <Save size={13} /> {savedAt ? "Re-save binder to loan" : "Save binder to loan"}
              </button>
              {!selectedLoan && <div className="text-[10px] text-[#A0A0A0]">Pick a loan above to save this binder to its file.</div>}
              {savedAt && <div className="text-[10.5px] text-[#0B6B4F]" data-testid="nsb-saved-note">✓ Saved copy on file · {fmtDate(String(savedAt).slice(0, 10))}</div>}
              <div className="flex items-center gap-2 pt-1">
                <input
                  value={faxTo}
                  onChange={(e) => setFaxTo(e.target.value)}
                  placeholder="Buyer fax +1…"
                  data-testid="nsb-fax-input"
                  className="flex-1 px-2 py-1.5 text-[12px] border border-[#D8D8CF] rounded-sm bg-white focus:border-[#0B4636] focus:outline-none"
                />
                <button
                  onClick={efax}
                  disabled={faxing}
                  data-testid="nsb-efax-btn"
                  className="flex items-center gap-1 px-3 py-1.5 rounded-sm border border-[#0B4636] text-[#0B4636] text-[12px] font-semibold hover:bg-[#0B4636] hover:text-white disabled:opacity-40 transition-colors"
                >
                  <Send size={12} /> eFax
                </button>
              </div>
              {statusMsg && <div className="text-[11px] text-[#0B4636]" data-testid="nsb-status-msg">{statusMsg}</div>}
              <div className="pt-2 border-t border-[#E5E5DF]">
                <button
                  onClick={mintCert}
                  disabled={minting}
                  data-testid="nsb-mint-btn"
                  className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-sm border border-[#B8860B] text-[#8a6508] bg-[#fbf6e7] text-[12px] font-semibold hover:bg-[#f5ecd0] disabled:opacity-40 transition-colors"
                >
                  <BadgeCheck size={13} /> {d.mintedSerial ? "Re-mint serial & QR" : "Mint serial & QR (on acceptance)"}
                </button>
                {d.mintedSerial && (
                  <div className="mt-2 text-[10.5px] text-[#0B6B4F] leading-snug" data-testid="nsb-minted-note">
                    ✓ Serial <strong>{d.mintedSerial}</strong> · verification QR affixed.{" "}
                    <a href={d.verifyUrl} target="_blank" rel="noreferrer" className="underline text-[#0B4636]" data-testid="nsb-verify-link">View public verify page</a>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="p-5 space-y-3">
            <span className="eyebrow text-[#5C5C5C]">Deal fields</span>
            <div>
              <span className="field-label">Buyer lookup (saved counterparty)</span>
              <div className="mt-0.5">
                <CounterpartyAutocomplete
                  theme="light"
                  placeholder="Search buyer / bank / counterparty…"
                  testid="nsb-buyer-lookup"
                  onSelect={(r) => setD((p) => ({
                    ...p,
                    buyerName: r.name || p.buyerName,
                    buyerAddress: r.address || p.buyerAddress,
                    buyerSigner: r.contact || r.signer || p.buyerSigner,
                    acceptanceBankName: r.bank || r.bank_name || p.acceptanceBankName,
                  }))}
                />
              </div>
            </div>
            {field("Seller (Holder)", "borrowerName")}
            {field("Buyer (Purchaser)", "buyerName")}
            {field("Buyer address", "buyerAddress")}
            {field("Buyer signer", "buyerSigner")}
            {field("Maker of Subject Note", "makerName")}
            {field("Maker SSN (last 4 shown, masked)", "makerSsn")}
            {field("Acceptance Bank", "acceptanceBankName")}
            <div className="grid grid-cols-2 gap-2">
              {field("Face value", "faceValue", "number")}
              {field("Purchase price", "purchasePrice", "number")}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {field("Coupon rate %", "couponRate", "number")}
              {field("Term (days)", "termDays", "number")}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {field("Execution date", "executionDate", "date")}
              {field("Maturity date", "maturityDate", "date")}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {field("Sale closing date", "saleClosingDate", "date")}
              {field("Governing law", "governingLaw")}
            </div>

            <div className="pt-2 mt-1 border-t border-[#E5E5DF]">
              <span className="eyebrow text-[#5C5C5C]">Registration (Bank Note)</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {field("Registration / EIN No.", "regNo")}
              {field("Intl. registration no. (optional)", "intlRegNo")}
            </div>

            <div className="pt-1">
              <span className="eyebrow text-[#5C5C5C]">Purchaser outbound wire (purchase price)</span>
            </div>
            {field("Bank", "purWireBank")}
            <div className="grid grid-cols-2 gap-2">
              {field("ABA / Routing", "purWireRouting")}
              {field("SWIFT / BIC", "purWireSwift")}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {field("Account name", "purWireAcctName")}
              {field("Account no.", "purWireAcctNo")}
            </div>

            <div className="pt-1">
              <span className="eyebrow text-[#5C5C5C]">Payable to purchaser at maturity</span>
            </div>
            {field("Bank", "payWireBank")}
            <div className="grid grid-cols-2 gap-2">
              {field("ABA / Routing", "payWireRouting")}
              {field("SWIFT / BIC", "payWireSwift")}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {field("Account name", "payWireAcctName")}
              {field("Account no.", "payWireAcctNo")}
            </div>
          </div>

          {/* E-signatures */}
          <div className="p-5 border-t border-[#E5E5DF]" data-testid="nsb-esign-panel">
            <div className="flex items-center justify-between mb-2">
              <span className="eyebrow text-[#5C5C5C]">E-Signatures · E-SIGN / UETA</span>
              <button onClick={applyAllSignatures} data-testid="nsb-sign-all" className="text-[11px] font-semibold text-[#0B4636] hover:underline">Sign all</button>
            </div>
            <p className="text-[10px] text-[#8A8A8A] mb-3 leading-snug">Type each party's name, then Sign — a cursive /s/ signature with a SHA-256 fingerprint is stamped into every document.</p>
            <div className="space-y-2">
              {SIG_ROLES.map((r) => (
                <div key={r.role} data-testid={`nsb-sig-row-${r.role}`}>
                  <span className="block text-[10px] uppercase tracking-[0.1em] text-[#8A8A8A]">{r.label}</span>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <input
                      value={d[r.nameField] ?? ""}
                      onChange={(e) => setField(r.nameField, e.target.value)}
                      placeholder="Full name"
                      data-testid={`nsb-sig-name-${r.role}`}
                      className="flex-1 px-2 py-1.5 text-[12.5px] border border-[#D8D8CF] rounded-sm bg-white focus:border-[#0B4636] focus:outline-none"
                    />
                    {hasSig(r.role) ? (
                      <button onClick={() => clearSignature(r.role)} data-testid={`nsb-sig-clear-${r.role}`} className="px-2 py-1.5 text-[11px] rounded-sm border border-[#0B6B4F] text-[#0B6B4F] bg-[#e8f5ef]">Signed ✓</button>
                    ) : (
                      <button onClick={() => applySignature(r.role, r.nameField)} data-testid={`nsb-sig-apply-${r.role}`} className="px-2 py-1.5 text-[11px] rounded-sm border border-[#0B4636] text-[#0B4636] hover:bg-[#0B4636] hover:text-white transition-colors">Sign</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </aside>

        {/* Main canvas */}
        <main className="col-span-8 xl:col-span-9 flex flex-col overflow-hidden">
          {/* Document chooser */}
          <div className="flex items-center gap-2 px-6 py-4 bg-[#F5F5F0] border-b border-[#E5E5DF] overflow-x-auto" data-testid="nsb-doc-tabs">
            <div className="flex items-center gap-2 mr-2 text-[#5C5C5C]">
              <Layers size={14} />
              <span className="eyebrow">Choose a document</span>
            </div>
            <button
              className={`tab-pill whitespace-nowrap shrink-0 ${activeDoc === "cover" ? "active" : ""}`}
              onClick={() => setActiveDoc("cover")}
              data-testid="nsb-tab-cover"
            >
              {activeDoc === "cover" && <CircleDot size={11} className="inline mr-1 -mt-0.5" />}
              Cover &amp; Contents
            </button>
            <button
              className={`tab-pill whitespace-nowrap shrink-0 ${activeDoc === "full" ? "active" : ""}`}
              onClick={() => setActiveDoc("full")}
              data-testid="nsb-tab-full"
            >
              {activeDoc === "full" && <CircleDot size={11} className="inline mr-1 -mt-0.5" />}
              Full Binder ▾
            </button>
            {SALE_DOCS.map((doc) => (
              <button
                key={doc.id}
                className={`tab-pill whitespace-nowrap shrink-0 ${activeDoc === doc.id ? "active" : ""}`}
                onClick={() => setActiveDoc(doc.id)}
                data-testid={`nsb-tab-${doc.id}`}
              >
                {activeDoc === doc.id && <CircleDot size={11} className="inline mr-1 -mt-0.5" />}
                {doc.label}
              </button>
            ))}
          </div>

          <div className="paper-canvas flex-1 overflow-auto" data-testid="nsb-canvas">
            {activeDoc === "cover" ? (
              <CoverPage d={d} />
            ) : activeDoc === "full" ? (
              <div data-testid="nsb-full-binder">
                <CoverPage d={d} />
                {SALE_DOCS.map(({ id, component: C }) => <C key={id} d={d} />)}
              </div>
            ) : ActiveComp ? (
              <ActiveComp d={d} />
            ) : null}
          </div>
        </main>
      </div>

      {/* Hidden print root */}
      <div className="print-root" style={{ display: printMode ? "block" : "none" }}>
        {printMode === "all" ? (
          <>
            <CoverPage d={d} />
            {SALE_DOCS.map(({ id, component: C }) => (
              <C key={id} d={d} />
            ))}
          </>
        ) : activeDoc === "cover" ? (
          <CoverPage d={d} />
        ) : ActiveComp ? (
          <ActiveComp d={d} />
        ) : null}
      </div>
    </div>
  );
}
